import React, { useMemo } from 'react';
import { Card, Select, Modal } from 'antd';
import { ExpandOutlined, CloseOutlined } from '@ant-design/icons';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import moment from 'moment';
import '../styles/trafficChart.css';

const { Option } = Select;

function TrafficChartPopup({
  type,
  id,
  stradeData,
  varchiData,
  onClose
}) {
  const [selectedMetric, setSelectedMetric] = React.useState('all');
  const [selectedVehicle, setSelectedVehicle] = React.useState('Auto');
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Get timeseries data for the selected item
  const timeseriesData = useMemo(() => {
    if (type === 'strada' && stradeData) {
      return stradeData.timeseriesByDirection[id] || [];
    } else if (type === 'varco' && varchiData) {
      // Filter by selected vehicle type for varchi
      return (varchiData.timeseriesByKey[id] || []).filter(
        item => !selectedVehicle || item.vehicle === selectedVehicle
      );
    }
    return [];
  }, [type, id, stradeData, varchiData, selectedVehicle]);

  // Transform data for the chart
  const chartData = useMemo(() => {
    if (!timeseriesData || timeseriesData.length === 0) return [];

    // Group by time index and format for chart
    const groupedData = {};

    timeseriesData.forEach(item => {
      const timeKey = item.t_idx;
      if (!groupedData[timeKey]) {
        groupedData[timeKey] = {
          time: moment.unix(item.unix_ts).format('HH:mm'),
          timestamp: item.unix_ts,
          t_idx: item.t_idx
        };
      }

      // Add metrics based on type
      if (type === 'strada') {
        groupedData[timeKey].Actual = Math.round(item.Actual * 10) / 10;
        groupedData[timeKey].Pred5min = Math.round(item.Pred5min * 10) / 10;
        groupedData[timeKey].Pred15min = Math.round(item.Pred15min * 10) / 10;
        groupedData[timeKey].Pred30min = Math.round(item.Pred30min * 10) / 10;
      } else if (type === 'varco') {
        // For varchi, show vehicle counts
        groupedData[timeKey].Actual = item.Actual;
        groupedData[timeKey].Pred5min = item.Pred5min;
        groupedData[timeKey].Pred15min = item.Pred15min;
        groupedData[timeKey].Pred30min = item.Pred30min;
      }
    });

    // Convert to array and sort by time
    return Object.values(groupedData).sort((a, b) => a.t_idx - b.t_idx);
  }, [timeseriesData, type]);

  // Get available vehicle types for varchi
  const vehicleTypes = useMemo(() => {
    if (type !== 'varco' || !varchiData || !varchiData.timeseriesByKey[id]) {
      return [];
    }
    const types = new Set();
    varchiData.timeseriesByKey[id].forEach(item => {
      if (item.vehicle) types.add(item.vehicle);
    });
    return Array.from(types);
  }, [type, id, varchiData]);

  // Get item details
  const itemDetails = useMemo(() => {
    if (type === 'strada' && stradeData) {
      return stradeData.byDirection[id];
    } else if (type === 'varco' && varchiData) {
      return varchiData.byKey[id];
    }
    return null;
  }, [type, id, stradeData, varchiData]);

  if (!itemDetails) {
    return null;
  }

  // Custom tick formatter for X-axis
  const formatXAxisTick = (tickItem) => {
    // Show every 2 hours worth of data points
    return tickItem;
  };

  // Render chart component matching RoadVelocita style
  const renderChart = (height = 300) => (
    <>
      {chartData?.length > 0 ? (
        <ResponsiveContainer width="100%" height={height}>
          {selectedMetric === 'all' ? (
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="time"
                tick={{ fontSize: 10, fill: "#666" }}
                axisLine={{ stroke: "#e0e0e0" }}
                interval={Math.max(0, Math.floor(chartData.length / 24))}
                angle={-45}
                textAnchor="end"
                tickMargin={5}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#666" }}
                axisLine={{ stroke: "#e0e0e0" }}
                label={{
                  value: type === 'strada' ? 'km/h' : 'Veicoli',
                  angle: -90,
                  position: "insideLeft",
                  offset: 10,
                  style: { fontSize: 12, fill: "#666", textAnchor: 'middle' },
                }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px'
                }}
              />
              <Line
                type="monotone"
                dataKey="Actual"
                stroke="#52c41a"
                strokeWidth={2}
                dot={false}
                name="Attuale"
              />
              <Line
                type="monotone"
                dataKey="Pred5min"
                stroke="#faad14"
                strokeWidth={2}
                dot={false}
                name="Pred 5min"
              />
              <Line
                type="monotone"
                dataKey="Pred15min"
                stroke="#1890ff"
                strokeWidth={2}
                dot={false}
                name="Pred 15min"
              />
              <Line
                type="monotone"
                dataKey="Pred30min"
                stroke="#722ed1"
                strokeWidth={2}
                dot={false}
                name="Pred 30min"
              />
            </LineChart>
          ) : (
            <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
              <defs>
                <linearGradient id="velocityGradientTraffic" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#A1E430" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#A1E430" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="time"
                tick={{ fontSize: 10, fill: "#666" }}
                axisLine={{ stroke: "#e0e0e0" }}
                interval={Math.max(0, Math.floor(chartData.length / 24))}
                angle={-45}
                textAnchor="end"
                tickMargin={5}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#666" }}
                axisLine={{ stroke: "#e0e0e0" }}
                label={{
                  value: type === 'strada' ? 'km/h' : 'Veicoli',
                  angle: -90,
                  position: "insideLeft",
                  offset: 10,
                  style: { fontSize: 12, fill: "#666", textAnchor: 'middle' },
                }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px'
                }}
              />
              <Area
                type="monotone"
                dataKey={selectedMetric}
                stroke="#A1E430"
                strokeWidth={2}
                fill="url(#velocityGradientTraffic)"
                name={
                  selectedMetric === 'Actual' ? 'Valore Attuale' :
                  selectedMetric === 'Pred5min' ? 'Previsione 5 min' :
                  selectedMetric === 'Pred15min' ? 'Previsione 15 min' :
                  'Previsione 30 min'
                }
                connectNulls={true}
              />
            </AreaChart>
          )}
        </ResponsiveContainer>
      ) : (
        <div className="chart-no-data">
          <p>⚠️ Nessun dato disponibile per questo elemento.</p>
        </div>
      )}
    </>
  );

  // Dynamic title based on type and item
  const modalTitle = type === 'strada'
    ? itemDetails.street_direction
    : `${itemDetails.gate} - ${itemDetails.direction}`;

  return (
    <>
      <div className="traffic-chart-popup-light">
        <Card
          title={
            <div className="traffic-chart-header-light">
              <div className="traffic-chart-title-light">
                <img
                  src={type === 'strada' ? '/data/strade/strade.png' : '/data/varchi/varchi.png'}
                  alt={type}
                  className="traffic-icon-small"
                />
                <h3>{modalTitle}</h3>
              </div>
              <button onClick={onClose} className="traffic-chart-close-light">
                <CloseOutlined />
              </button>
            </div>
          }
          className="traffic-chart-card-light"
        >
          <div className="traffic-chart-controls-light">
            <Select
              value={selectedMetric}
              onChange={setSelectedMetric}
              style={{ width: 200 }}
              className="metric-selector"
            >
              <Option value="all">Tutti i Valori</Option>
              <Option value="Actual">Valore Attuale</Option>
              <Option value="Pred5min">Previsione 5 min</Option>
              <Option value="Pred15min">Previsione 15 min</Option>
              <Option value="Pred30min">Previsione 30 min</Option>
            </Select>

            {type === 'varco' && vehicleTypes.length > 0 && (
              <Select
                value={selectedVehicle}
                onChange={setSelectedVehicle}
                style={{ width: 150 }}
                className="vehicle-selector"
                placeholder="Tipo Veicolo"
              >
                {vehicleTypes.map(vType => (
                  <Option key={vType} value={vType}>{vType}</Option>
                ))}
              </Select>
            )}
          </div>

          <div style={{ position: 'relative', marginTop: '20px' }}>
            {chartData?.length > 0 && (
              <button
                onClick={() => setIsExpanded(true)}
                className="expand-button"
                title="Espandi grafico"
              >
                <ExpandOutlined />
              </button>
            )}
            {renderChart(300)}
          </div>
        </Card>
      </div>

      {/* Expanded Chart Modal */}
      <Modal
        open={isExpanded}
        onCancel={() => setIsExpanded(false)}
        footer={null}
        width="95%"
        centered
        title={modalTitle}
        destroyOnClose
        zIndex={9999}
        style={{ top: 20 }}
      >
        <div style={{ padding: '20px 0' }}>
          {renderChart(500)}
        </div>
      </Modal>
    </>
  );
}

export default TrafficChartPopup;